import React, { useState, useEffect, useRef, useCallback } from 'react';
import Input from '../input/Input';
import { Checkbox } from '../checkbox/Checkbox';
import Divider from '../divider/Divider';
import { useClickedOutside } from '@app/react-common/hooks/useClickOutside';

import {
  countryCodeToDisplayNameMap,
  extendedCountryToStates,
  CountryCodeOptions,
  StateCode,
  countryNameToCountryCodeMap,
  getCountryCodeByCountryDisplayName,
  CountryRegionType,
  CountryCode,
} from '@pdqdev/pdq-shared/dist/utils/Country-utils';

interface Props {
  label?: string;
  multiSelect?: boolean;
  disabled?: boolean;
  preselectedCountriesAndRegionsValue: string;
  onSelect: (statesByCountry: string) => void;
  countries?: string[];
}

const CountriesAndRegionsDropdown: React.FC<Props> = ({
  label,
  multiSelect = false,
  disabled = false,
  preselectedCountriesAndRegionsValue,
  onSelect,
  countries = [],
}) => {
  const isFiltered = countries.length > 0;
  const shouldAutoExpand = countries.length === 1;
  const countriesKey = countries.join(',');
  const [isOpen, setIsOpen] = useState(false);
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});
  const [searchText, setSearchText] = useState('');
  const [countryOptions, setCountryOptions] = useState<[string, string][]>([]);
  const [selection, setSelection] = useState(normalizeCountriesAndRegionsValue(preselectedCountriesAndRegionsValue));
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, []);
  useClickedOutside(dropdownRef, handleClickOutside);

  useEffect(() => {
    setSelection(normalizeCountriesAndRegionsValue(preselectedCountriesAndRegionsValue));
  }, [preselectedCountriesAndRegionsValue]);

  /** reset list when opened */
  useEffect(() => {
    if (isOpen) {
      if (isFiltered) {
        const filteredOptions = Object.entries(countryCodeToDisplayNameMap).filter(([code]) =>
          countries.includes(code),
        ) as [string, string][];
        setCountryOptions(filteredOptions);
        if (shouldAutoExpand && countries[0]) {
          setExpandedStates({ [countries[0]]: true });
        }
      } else {
        setCountryOptions(Object.entries(countryCodeToDisplayNameMap));
      }
      setSearchText('');
    }
  }, [isOpen, isFiltered, countriesKey, shouldAutoExpand]);

  /** search filter */
  const handleSearch = (txt: string) => {
    setSearchText(txt);
    let options = Object.entries(countryCodeToDisplayNameMap);
    if (isFiltered) {
      options = options.filter(([code]) => countries.includes(code));
    }
    setCountryOptions(options.filter(([, n]) => n.toLowerCase().includes(txt.toLowerCase())) as [string, string][]);
  };
  const handleCountryCheck = (checked: boolean, country: CountryCodeOptions) => {
    if (disabled) return;
    setSelection((prev) => {
      let statesByCountry = { ...prev.statesByCountry };
      let countries = getCountryCodes(statesByCountry);

      if (checked) {
        if (!multiSelect) {
          countries = [country];
          statesByCountry = {
            [country]: extendedCountryToStates[country]?.map((s) => s.value) ?? [],
          };
        } else {
          if (!countries.includes(country)) countries.push(country);
          statesByCountry[country] = extendedCountryToStates[country]?.map((s) => s.value) ?? [];
        }
      } else {
        countries = countries.filter((c) => c !== country);
        delete statesByCountry[country];
      }

      /* notify the parent only when something actually changed */
      const next = { statesByCountry };
      onSelect(JSON.stringify(next.statesByCountry));
      return next;
    });
  };

  /** state check / uncheck */
  const handleStateCheck = (checked: boolean, state: StateCode, country: CountryCodeOptions) => {
    if (disabled) return;
    setSelection((prev) => {
      const statesByCountry = { ...prev.statesByCountry };
      statesByCountry[country] = statesByCountry[country] ?? [];
      const countries = getCountryCodes(statesByCountry);

      if (checked) {
        statesByCountry[country] = Array.from(new Set([...statesByCountry[country]!, state]));
        if (!countries.includes(country)) {
          countries.push(country);
        }
      } else {
        statesByCountry[country] = statesByCountry[country]!.filter((s) => s !== state);
        if (statesByCountry[country]!.length === 0) {
          statesByCountry[country] = [];
        }
      }

      const next = { statesByCountry };
      onSelect(JSON.stringify(next.statesByCountry));
      return next;
    });
  };

  const getRegionTypeLabel = (country: string) => {
    return country === CountryCode.CA ? CountryRegionType.PROVINCES : CountryRegionType.STATES;
  };

  const getSelectedText = () => {
    const countries = getCountryCodes(selection.statesByCountry);
    const countriesCount = countries.length;
    if (!countriesCount) return 'Select';
    if (countriesCount === 1) {
      const country = countries[0];
      const total = extendedCountryToStates[country]?.length ?? 0;
      const selected = selection.statesByCountry[country]?.length ?? 0;
      const name = countryCodeToDisplayNameMap[country];
      const label = getRegionTypeLabel(country);

      if (total && selected) {
        return `${name} (${selected} of ${total} ${label})`;
      }
      return name;
    }
    return `${countriesCount} Selected`;
  };

  function getCountryCodes(statesByCountry: Partial<Record<CountryCodeOptions, StateCode[]>>): CountryCodeOptions[] {
    // Object.keys returns string[], so cast the result
    return Object.keys(statesByCountry) as CountryCodeOptions[];
  }

  function normalizeCountriesAndRegionsValue(value: string | null | undefined): {
    statesByCountry: Partial<Record<CountryCodeOptions, StateCode[]>>;
  } {
    if (!value) return { statesByCountry: {} };

    /* ── try to treat it as JSON first ───────────────────────────── */
    try {
      const obj = JSON.parse(value) as Record<string, StateCode[]>;
      /* if JSON.parse succeeds we assume it’s already {code: […]} */
      const statesByCountry = obj as Partial<Record<CountryCodeOptions, StateCode[]>>;
      return { statesByCountry };
    } catch {
      /* fall through – plain string case */
    }

    /* ── plain country name → map to 2-letter code ───────────────── */
    const code =
      countryNameToCountryCodeMap[value.trim().toLowerCase()] ??
      (getCountryCodeByCountryDisplayName(value) as CountryCodeOptions);

    return { statesByCountry: code ? { [code]: [] } : {} };
  }

  return (
    <div className="container">
      {label && <label>{label}</label>}

      {/* main dropdown */}
      <div className="countries-dropdown" ref={dropdownRef}>
        <div className={`button-dropdown ${isOpen ? 'focus-border' : ''}`} onClick={() => setIsOpen((o) => !o)}>
          <span>{getSelectedText()}</span>
          <div className={`pdq-react-dropdown__icon ${isOpen ? 'rotate' : ''}`} />
        </div>

        {isOpen && (
          <div className="countries-dropdown__popup">
            {/* search - hide when countries are filtered */}
            {!isFiltered && (
              <>
                <Input
                  className="countries-dropdown__search-input"
                  placeholder="Search"
                  type="text"
                  startIcon={<img src="./assets/icons/icon-search-hover.svg" alt="" />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <Divider variant="solid" className="countries-dropdown__divider" />
              </>
            )}

            {/* country list */}
            <div className={`countries-dropdown__list` + (disabled ? ' countries-dropdown__list--disabled' : '')}>
              {countryOptions.map(([code, name]) => {
                const countryStates = extendedCountryToStates[code] || [];
                const hasStates = countryStates.length > 0;
                const isExpanded = expandedStates[code];
                const selectedStates = selection.statesByCountry[code] ?? [];
                const selectedCountries = getCountryCodes(selection.statesByCountry);
                const isSelected = selectedCountries.includes(code as CountryCodeOptions);

                return (
                  <div key={code}>
                    <div
                      className={
                        `pdq-react-dropdown__item` +
                        (multiSelect ? ' multiSelect' : '') +
                        (isSelected ? ' selected' : '')
                      }>
                      {/* header keeps name + count in reading order */}
                      <div
                        className="countries-dropdown__item-header"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setExpandedStates((prev) => ({
                            ...prev,
                            [code]: !prev[code],
                          }));
                        }}>
                        {/* checkbox + country name */}
                        <label onClick={(e: React.MouseEvent) => e.stopPropagation()} className="country-label">
                          <Checkbox
                            checked={isSelected}
                            disabled={disabled}
                            onChange={(checked: boolean) => handleCountryCheck(checked, code as CountryCodeOptions)}
                          />
                          <span>{name}</span>
                        </label>

                        {/* count + chevron */}
                        {hasStates && (
                          <div className="countries-dropdown__list-item-expander">
                            <span>
                              {`${selectedStates.length} of ${countryStates.length} ${getRegionTypeLabel(code)}`}
                            </span>
                            {/* hide chevron when auto-expanded (single country filter) */}
                            {!shouldAutoExpand && (
                              <div className={`pdq-react-dropdown__icon expander-icon ${isExpanded ? 'rotate' : ''}`} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* nested states */}
                    {hasStates && (isExpanded || shouldAutoExpand) && (
                      <div className="states__list">
                        {countryStates.map((state) => {
                          const val = state.value;
                          const isStateSelected = (selection.statesByCountry[code] || []).includes(val);

                          return (
                            <label
                              key={val}
                              className={
                                `pdq-react-dropdown__item` +
                                (multiSelect ? ' multiSelect' : '') +
                                (isStateSelected ? ' selected' : '') +
                                ' state-label'
                              }
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                              <Checkbox
                                checked={isStateSelected}
                                disabled={disabled}
                                onChange={(checked: boolean) =>
                                  handleStateCheck(checked, val, code as CountryCodeOptions)
                                }
                              />
                              <span>{state.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CountriesAndRegionsDropdown;
